/**
 * FieldColumnsModal (Unit 4 Phase 2) — the per-site column picker.
 *
 * NO ModalShell. That shared component exposes only title / children / footer
 * and has no header slot, and this header needs three controls beside the
 * title. It is also shared with the warehouse and inventory modals, so
 * widening it would put those in this unit's blast radius. This component
 * owns its own chrome instead.
 *
 * NO APPLY STEP. Close, ✕ and Done all call the same onClose; every toggle
 * lands live on tap. There is deliberately no Cancel and nothing to confirm —
 * a rep hiding ten columns mid-rack-walk wants each tap to take effect, not a
 * transaction to remember to commit. Dismissal is therefore never a decision,
 * which is what lets Android's hardware back be a first-class close path
 * rather than an ambiguous one.
 *
 * TOGGLE POLARITY: ON = VISIBLE. The stored set is `hidden`, so the switch is
 * the inverse of membership — checked === !hidden.has(key). Stating it here
 * because the two read opposite and the inversion is easy to lose.
 *
 * THE TOOL ID LOCK is read from the hook's exported NEVER_HIDDEN rather than
 * restated as a literal, so this component and useFieldHiddenFields cannot
 * disagree about which keys are locked. The row still renders — a rep should
 * see that TOOL ID exists and is deliberately pinned, not wonder where it
 * went — with a disabled switch and no tap handler.
 */

import React from 'react';
import { NEVER_HIDDEN } from '../hooks/useFieldHiddenFields';
import './FieldColumnsModal.css';

/* Approved badge mapping. FLAG rather than BOOL because the grid already
   calls these columns flags (INV_VIEW_COLUMNS kind:'flag'); LIST rather than
   ENUM because a rep meets those as a pick list, not as a type. A field with
   NO kind is a read-only display row — that absence is the discriminator,
   and it is reliable: every BASE_FIELDS entry has a kind, no
   READONLY_DISPLAY_FIELDS entry does. childValue (P1-2 piece 1: PART NUMBER
   1-3, ASSET TAG 1-3) badges as a text field — its cells show text — rather
   than falling through to an unknown-kind CHILDVALUE badge. */
const BADGE_BY_KIND = {
  text: { label: 'TEXT', slug: 'text' },
  number: { label: 'NUM', slug: 'num' },
  bool: { label: 'FLAG', slug: 'flag' },
  enum: { label: 'LIST', slug: 'list' },
  childValue: { label: 'TEXT', slug: 'text' },
};
const READONLY_BADGE = { label: 'READ ONLY', slug: 'readonly' };

function badgeFor(field) {
  if (!field.kind) return READONLY_BADGE;
  // Unknown kind falls back to its own name rather than to TEXT — a new kind
  // added upstream should look wrong here, not quietly mislabel itself.
  return BADGE_BY_KIND[field.kind] || { label: String(field.kind).toUpperCase(), slug: 'text' };
}

export default function FieldColumnsModal({
  fields, hidden, onToggle, onShowAll, onHideAll, onClose,
}) {
  // None and All act on THIS VIEW's keys — the `fields` this modal renders —
  // and nothing else (hide guard). The hidden set is shared by INV and ALL, so
  // the hook leaves every key not handed over here exactly as it is.
  const viewKeys = fields.map((f) => f.key);

  // "None" hands over the view's keys minus the locked ones. The hook
  // filters these out again on the way in (sanitize/NEVER_HIDDEN) — belt and
  // braces, because a bulk action is exactly where a lock gets forgotten.
  const handleNone = () => {
    onHideAll(viewKeys.filter((k) => !NEVER_HIDDEN.has(k)));
  };
  const handleAll = () => {
    onShowAll(viewKeys);
  };

  return (
    <div
      className="fcm"
      data-testid="field-columns-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Columns"
    >
      <div className="fcm-header">
        <h2 className="fcm-title">Columns</h2>
        <button
          type="button"
          className="fcm-bulk"
          data-testid="field-columns-all"
          onClick={handleAll}
        >
          All
        </button>
        <button
          type="button"
          className="fcm-bulk"
          data-testid="field-columns-none"
          onClick={handleNone}
        >
          None
        </button>
        <button
          type="button"
          className="fcm-close"
          data-testid="field-columns-close"
          onClick={onClose}
          aria-label="Close columns"
        >
          ✕
        </button>
      </div>

      {/* Rendered in the order `fields` arrives and NEVER re-sorted. The list
          is derived from module-scope manifests, so a toggle changes one
          row's switch and nothing else — no row moves under the finger that
          just tapped it, and the next tap lands where the rep aimed. */}
      <div className="fcm-body">
        {fields.map((field) => {
          const locked = NEVER_HIDDEN.has(field.key);
          const checked = !hidden.has(field.key);
          const badge = badgeFor(field);
          return (
            <button
              key={field.key}
              type="button"
              role="switch"
              aria-checked={checked}
              disabled={locked}
              className={`fcm-row${locked ? ' fcm-row-locked' : ''}`}
              data-testid={`field-columns-row-${field.key}`}
              onClick={locked ? undefined : () => onToggle(field.key)}
            >
              <span className={`fcm-badge fcm-badge-${badge.slug}`}>{badge.label}</span>
              <span className="fcm-label">
                {field.label}
                {locked && <span className="fcm-lock-note">(always visible)</span>}
              </span>
              <span
                className={`fcm-switch${checked ? ' fcm-switch-on' : ''}`}
                aria-hidden="true"
              >
                <span className="fcm-switch-knob" />
              </span>
            </button>
          );
        })}
      </div>

      <div className="fcm-footer">
        <button
          type="button"
          className="fcm-done"
          data-testid="field-columns-done"
          onClick={onClose}
        >
          Done
        </button>
      </div>
    </div>
  );
}
